import React, { useEffect, useState } from 'react';
import SharedHeaderHomeowner from './SharedHeaderHomeowner';
import { useApi } from '../lib/useApi';
import { getApiUrl } from '../lib/getApiUrl';

type PitchRow = { pitch: string; areaSqFt: number; percent: number };
type Report = {
  id: string;
  address: string;
  reportDate?: string;
  eagleviewReportNo?: string;
  totalAreaSqFt?: number;
  totalSquares?: string;
  predominantPitch?: string;
  stories?: number;
  facetCount?: number;
  ridgesFt?: number;
  hipsFt?: number;
  valleysFt?: number;
  rakesFt?: number;
  eavesFt?: number;
  dripEdgeFt?: number;
  flashingFt?: number;
  stepFlashingFt?: number;
  complexity?: 'SIMPLE'|'NORMAL'|'COMPLEX'|'UNKNOWN';
  pitchBreakdownJson?: PitchRow[];
  pdfUrl?: string;
};

interface HomeownerReportPageProps {
  onNavigate?: (screen: string) => void;
}

export default function HomeownerReportPage({ onNavigate }: HomeownerReportPageProps) {
  const [report, setReport] = useState<Report|null>(null);
  const [loading, setLoading] = useState(true);
  const api = useApi();

  // Get homeownerId from current project data
  const getHomeownerId = async () => {
    try {
      // Get the active project ID
      const activeProjectId = localStorage.getItem('active_project_id');
      if (!activeProjectId) return null;
      
      // CB:BEGIN use-api-helper-projects
      // Use useApi() helper which automatically includes Authorization Bearer token
      const projectData = await api.get(`/api/projects/${activeProjectId}`);
      return projectData.homeownerId || projectData.homeowner?.id;
      // CB:END use-api-helper-projects
    } catch (error) {
      console.error('Error getting homeowner ID:', error);
    }
    return null;
  };

  const [homeownerId, setHomeownerId] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        // First get the homeowner ID
        const id = await getHomeownerId();
        if (!id) {
          console.error('No homeowner ID found');
          setLoading(false);
          return;
        }
        
        setHomeownerId(id);
        
        // CB:BEGIN use-api-helper-reports
        // Use useApi() helper which automatically includes Authorization Bearer token
        const data = await api.get(`/api/reports/latest/${id}`);
        if (data.report) {
          // Convert relative pdfUrl to full URL if needed
          if (data.report.pdfUrl && !data.report.pdfUrl.startsWith('http')) {
            data.report.pdfUrl = getApiUrl(data.report.pdfUrl);
          }
          setReport(data.report);
        }
        // CB:END use-api-helper-reports
      } catch (error) {
        console.error('Failed to fetch report:', error);
      } finally {
        setLoading(false);
      }
    })();
  }, [api]);

  const handleBackToDashboard = () => {
    if (onNavigate) {
      onNavigate('customer-dashboard');
    } else {
      // Fallback navigation
      window.history.back();
    }
  };

  const handleContinueToProject = () => {
    // Store the report data for quote generation
    if (report) {
      localStorage.setItem('current_project_report', JSON.stringify(report));
      localStorage.setItem('current_project_homeowner_id', homeownerId || '');
    }
    
    if (onNavigate) {
      onNavigate('quote-generation');
    } else {
      // Fallback navigation
      window.location.href = '/quote-generation';
    }
  };

  if (loading) return (
    <div>
      <SharedHeaderHomeowner onNavigate={onNavigate || (() => {})} />
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading report...</p>
          </div>
        </div>
      </div>
    </div>
  );

  if (!report) return (
    <div>
      <SharedHeaderHomeowner onNavigate={onNavigate || (() => {})} />
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center max-w-2xl mx-auto">
            <div className="bg-white rounded-lg shadow p-8">
              <h1 className="text-2xl font-bold text-gray-800 mb-4">No Report Found</h1>
              <p className="text-gray-600">No EagleView report has been uploaded for this property yet.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <SharedHeaderHomeowner onNavigate={onNavigate || (() => {})} />
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-6xl mx-auto">
            {/* Header Section */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h1 className="text-3xl font-bold text-gray-800 mb-2">Roof Report</h1>
                  <p className="text-gray-600">Professional analysis of your property's roofing requirements</p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={handleBackToDashboard}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                  >
                    ← Back to Dashboard
                  </button>
                  <button
                    onClick={handleContinueToProject}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                  >
                    Continue to Project Submission →
                  </button>
                </div>
              </div>
            </div>

            {/* Property Overview */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Property Overview</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <p className="text-sm text-gray-500 font-medium mb-1">Property Address</p>
                  <p className="font-semibold text-gray-800">{report.address}</p>
                </div>
                {report.reportDate && (
                  <div>
                    <p className="text-sm text-gray-500 font-medium mb-1">Report Date</p>
                    <p className="font-semibold text-gray-800">
                      {new Date(report.reportDate).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </p>
                  </div>
                )}
                {report.eagleviewReportNo && (
                  <div>
                    <p className="text-sm text-gray-500 font-medium mb-1">EagleView Report #</p>
                    <p className="font-semibold text-gray-800 font-mono">{report.eagleviewReportNo}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <p className="text-sm text-gray-500 font-medium mb-1">Total Roof Area</p>
                <p className="text-2xl font-bold text-gray-800 mb-1">
                  {report.totalAreaSqFt?.toLocaleString()}
                </p>
                <p className="text-sm text-gray-600">square feet</p>
                {report.totalSquares && (
                  <p className="text-xs text-gray-500 mt-1">({report.totalSquares} squares)</p>
                )}
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <p className="text-sm text-gray-500 font-medium mb-1">Predominant Pitch</p>
                <p className="text-2xl font-bold text-gray-800 mb-1">{report.predominantPitch || '—'}</p>
                <p className="text-sm text-gray-600">roof slope</p>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <p className="text-sm text-gray-500 font-medium mb-1">Roof Facets</p>
                <p className="text-2xl font-bold text-gray-800 mb-1">{report.facetCount ?? '—'}</p>
                <p className="text-sm text-gray-600">sections</p>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <p className="text-sm text-gray-500 font-medium mb-1">Complexity Level</p>
                <p className="text-lg font-bold text-gray-800 mb-1">{report.complexity ?? 'UNKNOWN'}</p>
                <p className="text-sm text-gray-600">structure level</p>
              </div>
            </div>

            {/* Detailed Measurements */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Detailed Measurements</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: 'Ridges', value: report.ridgesFt },
                  { label: 'Hips', value: report.hipsFt },
                  { label: 'Valleys', value: report.valleysFt },
                  { label: 'Rakes', value: report.rakesFt },
                  { label: 'Eaves/Starter', value: report.eavesFt },
                  { label: 'Drip Edge', value: report.dripEdgeFt },
                  { label: 'Flashing', value: report.flashingFt },
                  { label: 'Step Flashing', value: report.stepFlashingFt }
                ].map((item, index) => (
                  <div key={index} className="text-center p-3 bg-gray-50 rounded-md">
                    <p className="text-sm text-gray-500 font-medium mb-1">{item.label}</p>
                    <p className="text-lg font-semibold text-gray-800">{item.value ?? '—'} ft</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Pitch Breakdown */}
            {report.pitchBreakdownJson?.length ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4">Pitch Breakdown</h2>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pitch</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Area (sq ft)</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">% of Roof</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {report.pitchBreakdownJson.map((row, i) => (
                        <tr key={i} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{row.pitch}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{row.areaSqFt.toLocaleString()}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{row.percent}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}

            {/* PDF Link */}
            {report.pdfUrl && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4">Original Report</h2>
                <p className="text-gray-600 mb-4">Access the complete EagleView PDF report for detailed analysis and reference.</p>
                <a
                  href={report.pdfUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
                >
                  View Original PDF Report
                </a>
              </div>
            )}

            {/* Bottom Navigation */}
            <div className="mt-12 pt-8 border-t border-gray-200">
              <div className="text-center mb-6">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Ready to Get Started?</h3>
                <p className="text-gray-600">Use this report data to generate accurate project quotes and move forward with your roofing project.</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={handleBackToDashboard}
                  className="px-6 py-3 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                >
                  ← Back to Dashboard
                </button>
                <button
                  onClick={handleContinueToProject}
                  className="px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                >
                  Continue to Project Submission →
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
